import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { ILegalDocument, LegalDocumentSlug } from '../../services/exports/Interfaces';
import { useGetLegalDocumentQuery } from '../../store/api-slices/CompaniesApiSlice';

interface UseLegalDocument {
  generated?: ILegalDocument;
  /** A document is expected but has not arrived. Render neither bundle nor document yet. */
  isPending: boolean;
}

/**
 * The restaurant's own generated legal document, when there is one.
 *
 * `isPending` exists because the two "no document" cases need opposite handling. If the
 * provider has the feature and the request is still in flight, showing the jurisdiction
 * bundle means flashing the platform's own legal text at a customer before the
 * restaurant's replaces it — which is most of what this feature set out to stop. If the
 * provider does not have it, there is nothing to wait for and the bundle must render
 * immediately, which is the case for almost every restaurant.
 *
 * The provider flag arrives with the company payload, so that distinction is known
 * before this request resolves.
 *
 * A failed request is NOT pending: it falls through to the bundle, because a legal page
 * must never be blank and there is nothing better to show.
 *
 * `lang` is i18next's resolved language, region tag and all: the backend normalises it,
 * and stripping the region here would duplicate that rule in a second place.
 */
export default function useLegalDocument(document: LegalDocumentSlug): UseLegalDocument {
  const { i18n } = useTranslation();

  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const expected = company?.service_provider?.has_generated_legal_documents === true;

  const { data, isError, isUninitialized } = useGetLegalDocumentQuery(
    { slug: company?.slug, document, lang: i18n.language },
    { skip: !company?.slug || !expected },
  );

  return {
    generated: data,
    // Derived from the query's own lifecycle, not from `!data && !isError`. A skipped
    // query has neither, so a flagged provider whose company has no slug yet would have
    // pinned isPending on permanently and left the page showing a skeleton with no
    // escape — which is the blank legal page this hook exists to prevent.
    isPending: expected && !isUninitialized && !data && !isError,
  };
}
